/**
 * ============================================================================
 * KbPermissionsPage — Fase D
 * ----------------------------------------------------------------------------
 * Lista todos os perfis de permissão do tenant e mostra, para cada perfil,
 * os spaces/coleções/páginas do KB concedidos a ele. Permite ao admin revogar
 * concessões existentes; para conceder novas, o admin usa o botão "Gerenciar
 * acesso" no próprio space/coleção/página (KbAclManager).
 *
 * CHANGELOG:
 *   RELEASE: 2026-07-08 v1.0.0 - Versão inicial (Fase D).
 * ============================================================================
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ShieldCheck, KeyRound, Trash2, ChevronDown, ChevronRight, FileText, FolderTree, BookOpen } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "@/hooks/use-toast";
import { logger } from "@/lib/logger";

interface ProfileScopeRow {
  acl_id: string;
  scope: "page" | "collection" | "space";
  scope_id: string;
  scope_name: string | null;
  access_level: "read" | "comment" | "edit" | "admin";
  created_at: string;
}

const LEVEL_LABEL: Record<ProfileScopeRow["access_level"], string> = {
  read: "Leitura",
  comment: "Comentário",
  edit: "Edição",
  admin: "Admin",
};

const SCOPE_ICON = {
  page: FileText,
  collection: FolderTree,
  space: BookOpen,
} as const;

const SCOPE_LABEL = {
  page: "Página",
  collection: "Coleção",
  space: "Space",
} as const;

/** Carrega perfis ativos do tenant via RPC existente. */
function usePermissionProfilesList() {
  const { currentTenant } = useAuth();
  return useQuery({
    queryKey: ["kb-permissions", "profiles", currentTenant?.id],
    enabled: !!currentTenant?.id,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_permission_profiles_list", {
        p_tenant_hint: currentTenant!.id,
      } as any);
      if (error) throw error;
      return ((data ?? []) as any[]).map((p) => ({
        id: p.id as string,
        name: p.name as string,
        description: p.description as string | null,
        is_active: !!p.is_active,
      }));
    },
    staleTime: 60_000,
  });
}

/** Concessões KB de um perfil (Fase D). */
function useProfileScopes(profileId: string | null) {
  return useQuery({
    queryKey: ["kb-permissions", "profile-scopes", profileId],
    enabled: !!profileId,
    queryFn: async () => {
      const { data, error } = await (supabase.rpc as any)("kb_list_profile_scopes", {
        p_profile_id: profileId,
      });
      if (error) throw error;
      return (data ?? []) as ProfileScopeRow[];
    },
    staleTime: 20_000,
  });
}

function useRevoke() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (aclId: string) => {
      const { data, error } = await (supabase.rpc as any)("kb_revoke_page_access", {
        p_acl_id: aclId,
      });
      if (error) throw error;
      return data as boolean;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["kb-permissions"] });
      qc.invalidateQueries({ queryKey: ["kb-acl"] });
      toast({ title: "Concessão removida" });
    },
    onError: (e: any) => {
      logger.error("[KbPermissionsPage] revoke error", e);
      toast({
        title: "Falha ao remover",
        description: e?.message ?? "Erro desconhecido",
        variant: "destructive",
      });
    },
  });
}

// ---------------------------------------------------------------------------
// Painel por perfil
// ---------------------------------------------------------------------------
function ProfileCard({
  profile,
}: {
  profile: { id: string; name: string; description: string | null; is_active: boolean };
}) {
  const [open, setOpen] = useState(false);
  const { data: rows = [], isLoading } = useProfileScopes(open ? profile.id : null);
  const revoke = useRevoke();

  const grouped = useMemo(() => {
    const g: Record<ProfileScopeRow["scope"], ProfileScopeRow[]> = {
      space: [],
      collection: [],
      page: [],
    };
    rows.forEach((r) => g[r.scope].push(r));
    return g;
  }, [rows]);

  return (
    <Card className="p-4">
      <button
        type="button"
        className="w-full flex items-center gap-2 text-left"
        onClick={() => setOpen((v) => !v)}
      >
        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        <KeyRound className="h-4 w-4 text-primary" />
        <div className="flex-1 min-w-0">
          <div className="font-medium truncate">{profile.name}</div>
          {profile.description && (
            <div className="text-xs text-muted-foreground truncate">{profile.description}</div>
          )}
        </div>
        {!profile.is_active && (
          <Badge variant="outline" className="text-[10px]">Inativo</Badge>
        )}
      </button>

      {open && (
        <div className="mt-3 space-y-3">
          {isLoading && (
            <>
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
            </>
          )}

          {!isLoading && rows.length === 0 && (
            <p className="text-xs text-muted-foreground italic">
              Este perfil não possui concessões diretas na Base de Conhecimento.
              Para conceder acesso, abra o space/coleção/página desejado e use
              "Gerenciar acesso" selecionando o tipo <b>Perfil de permissão</b>.
            </p>
          )}

          {(["space", "collection", "page"] as const).map((sc) => {
            const items = grouped[sc];
            if (items.length === 0) return null;
            const Icon = SCOPE_ICON[sc];
            return (
              <div key={sc}>
                <div className="flex items-center gap-2 mb-1 text-xs font-semibold text-muted-foreground uppercase">
                  <Icon className="h-3.5 w-3.5" /> {SCOPE_LABEL[sc]} ({items.length})
                </div>
                <div className="space-y-1">
                  {items.map((r) => (
                    <div
                      key={r.acl_id}
                      className="flex items-center gap-2 rounded border px-2 py-1.5 text-xs"
                    >
                      <span className="flex-1 truncate font-medium">
                        {r.scope_name ?? r.scope_id.slice(0, 8).toUpperCase()}
                      </span>
                      <Badge variant="secondary" className="text-[10px]">
                        {LEVEL_LABEL[r.access_level]}
                      </Badge>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={() => revoke.mutate(r.acl_id)}
                        disabled={revoke.isPending}
                        aria-label="Revogar"
                      >
                        <Trash2 className="h-3.5 w-3.5 text-destructive" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Página
// ---------------------------------------------------------------------------
export default function KbPermissionsPage() {
  const { data: profiles = [], isLoading } = usePermissionProfilesList();

  return (
    <div className="flex h-full flex-col gap-4 p-4 md:p-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-primary" />
            <h1 className="text-2xl font-semibold">Acessos por perfil</h1>
          </div>
          <p className="text-sm text-muted-foreground">
            Visão reversa da Fase D — quais spaces, coleções e páginas cada
            perfil de permissão pode acessar na Base de Conhecimento.
          </p>
        </div>
        <Button variant="outline" size="sm" asChild>
          <Link to="/tenant/kb">Voltar</Link>
        </Button>
      </div>

      {isLoading && (
        <div className="space-y-2">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      )}

      {!isLoading && profiles.length === 0 && (
        <Card className="p-6 text-center text-sm text-muted-foreground">
          Nenhum perfil de permissão cadastrado neste tenant.
        </Card>
      )}

      <div className="space-y-2">
        {profiles.map((p) => (
          <ProfileCard key={p.id} profile={p} />
        ))}
      </div>
    </div>
  );
}
